/**
 * Reusable per-student grade computation shared by ReportCardPage and
 * RankingsPage. Given a student-scoped slice of the class rankings payload,
 * returns per-category × per-period grades, per-period averages and the
 * weighted Final Grade — using the same math as the Report Card page.
 */
import {
  categoryGradeForStudent, periodGradeAvgCategories,
  finalGradeForStudent, directPercentEquivalent,
} from "@/lib/grading";

/**
 * @param {object} args
 *  - categories, periods, settings, scale_ranges: shared setup
 *  - tasks: assessment tasks for the section
 *  - studentScores: scores for THIS student only [{assessment_task_id, score}]
 *  - studentDirects: direct grades for THIS student only
 */
export function computeStudentGrades({
  categories, periods, settings, scale_ranges,
  tasks, studentScores, studentDirects,
}) {
  const scoreByTask = new Map();
  for (const s of studentScores) scoreByTask.set(s.assessment_task_id, s.score);
  const tasksByCompByPeriod = {};
  for (const t of tasks) {
    (tasksByCompByPeriod[t.grading_period_id] ||= {});
    (tasksByCompByPeriod[t.grading_period_id][t.assessment_component_id] ||= []).push(t);
  }
  const directByLvl = {};
  for (const d of studentDirects) {
    (directByLvl[d.level] ||= {});
    (directByLvl[d.level][d.target_id] ||= {});
    directByLvl[d.level][d.target_id][d.grading_period_id] = d;
  }

  const isDirect = settings.grading_mode === "direct";
  const directLevel = settings.direct_assessment_level || "category";

  const overallFor = (lvl, id) => {
    const d = directByLvl[lvl]?.[id]?.["__overall__"];
    if (!d) return null;
    return directPercentEquivalent(
      d.direct_grade_value, d.direct_grade_code,
      settings.grading_scale_type, scale_ranges,
      settings.custom_percentage_range_enabled,
    );
  };

  const categoryRows = categories.map((cat) => {
    const perPeriod = periods.map((p) => {
      if (isDirect) {
        if (directLevel === "category") {
          const d = directByLvl["category"]?.[cat.id]?.[p.id];
          if (!d) return { period: p, grade: null };
          return {
            period: p,
            grade: directPercentEquivalent(
              d.direct_grade_value, d.direct_grade_code,
              settings.grading_scale_type, scale_ranges,
              settings.custom_percentage_range_enabled,
            ),
          };
        }
        if (directLevel === "component") {
          const vals = cat.components
            .map((c) => directByLvl["component"]?.[c.id]?.[p.id])
            .filter(Boolean)
            .map((d) => directPercentEquivalent(
              d.direct_grade_value, d.direct_grade_code,
              settings.grading_scale_type, scale_ranges,
              settings.custom_percentage_range_enabled,
            ))
            .filter((v) => v != null);
          if (!vals.length) return { period: p, grade: null };
          return { period: p, grade: Math.round((vals.reduce((a, v) => a + v, 0) / vals.length) * 100) / 100 };
        }
        return { period: p, grade: null };
      }
      const compMap = tasksByCompByPeriod[p.id] || {};
      const grade = categoryGradeForStudent(cat.components, compMap, scoreByTask);
      return { period: p, grade };
    });
    const grades = perPeriod.map((x) => x.grade).filter((g) => g != null);
    const computedOverall = grades.length
      ? Math.round((grades.reduce((a, g) => a + g, 0) / grades.length) * 100) / 100
      : null;
    let manualOverall = null;
    if (isDirect) {
      if (directLevel === "category") {
        manualOverall = overallFor("category", cat.id);
      } else if (directLevel === "component") {
        const vals = cat.components.map((c) => overallFor("component", c.id)).filter((v) => v != null);
        if (vals.length) manualOverall = Math.round((vals.reduce((a, v) => a + v, 0) / vals.length) * 100) / 100;
      }
    }
    return { ...cat, perPeriod, overall: manualOverall ?? computedOverall };
  });

  const periodAvgs = periods.map((p, pi) => {
    const grades = categoryRows.map((cr) => cr.perPeriod[pi]?.grade);
    return { period: p, grade: periodGradeAvgCategories(grades) };
  });

  const finalGrade = finalGradeForStudent(
    periodAvgs.map((pa) => ({ grade: pa.grade, period_percentage: pa.period.period_percentage }))
  );

  return { categoryRows, periodAvgs, finalGrade };
}
